import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Avatar,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  AddBusiness,
  Business,
  Notifications
} from '@mui/icons-material';
// import { useResponsive } from '../../hooks/useResponsive';
import { useAuth } from '../../contexts/AuthContext';
import { organizationService } from '../../services/organizationService';
import { OrganizationDTO, OrganizationType } from '../../types/organization';
import { useNavigate } from 'react-router-dom';
import { UserType } from '../../types/auth';

export const ManagerDashboardPage: React.FC = () => {
  const { state } = useAuth();
  const navigate = useNavigate();

  const authenticatedUser = state.user; // Получаем весь объект AuthUser
  const userId = authenticatedUser?.type === UserType.MANAGER ? authenticatedUser.manager?.id : authenticatedUser?.user?.id; // Корректно получаем id для менеджера или исполнителя
  console.log('ManagerDashboardPage mounted/rendered. Current userId:', userId); // Add console log
  const [companies, setCompanies] = useState<OrganizationDTO[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [openCreateModal, setOpenCreateModal] = useState<boolean>(false);
  const [newCompanyName, setNewCompanyName] = useState<string>('');
  const [newCompanyInn, setNewCompanyInn] = useState<string>('');
  const [newCompanyType, setNewCompanyType] = useState<OrganizationType>(OrganizationType.IndividualEntrepreneur); // Default to IndividualEntrepreneur
  const [newCompanyDirectorFullName, setNewCompanyDirectorFullName] = useState<string>('');
  const [newCompanyLegalAddress, setNewCompanyLegalAddress] = useState<string>('');
  const [newCompanyKpp, setNewCompanyKpp] = useState<string>('');
  const [newCompanyOgrn, setNewCompanyOgrn] = useState<string>('');
  const [creatingCompany, setCreatingCompany] = useState<boolean>(false);
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  const fetchCompanies = useCallback(async () => {
    if (!userId) {
      console.log('fetchCompanies: userId is null, returning.'); // Add console log
      return; // Don't fetch if userId is not available
    }
    console.log('fetchCompanies: Attempting to fetch companies for userId:', userId); // Add console log
    try {
      setLoading(true);
      const data = await organizationService.getOrganizations();
      setCompanies(data);
    } catch (err) {
      setError('Не удалось загрузить компании.');
      console.error('Error fetching organizations:', err);
    } finally {
      setLoading(false);
    }
  }, [userId]); // userId является зависимостью для useCallback

  useEffect(() => {
    if (!userId) return;
    fetchCompanies();
  }, [userId, fetchCompanies]);

  const handleOpenCreateModal = () => {
    setOpenCreateModal(true);
  };

  const handleCloseCreateModal = () => {
    setOpenCreateModal(false);
    setNewCompanyName('');
    setNewCompanyInn('');
    setNewCompanyType(OrganizationType.IndividualEntrepreneur);
    setNewCompanyDirectorFullName('');
    setNewCompanyLegalAddress('');
    setNewCompanyKpp('');
    setNewCompanyOgrn('');
  };

  const handleCreateCompany = async () => {
    if (!newCompanyName.trim()) {
      setSnackbarMessage('Название компании не может быть пустым.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    if (!newCompanyInn.trim()) {
      setSnackbarMessage('ИНН компании не может быть пустым.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    try {
      setCreatingCompany(true);
      console.log('Attempting to create company:', { 
        id: '00000000-0000-0000-0000-000000000000', // Placeholder for new ID, backend should generate
        name: newCompanyName, 
        inn: newCompanyInn,
        icon: '', // Default empty icon
        type: newCompanyType,
        directorFullName: newCompanyDirectorFullName,
        legalAddress: newCompanyLegalAddress,
        kpp: newCompanyKpp,
        ogrn: newCompanyOgrn,
      }); // Debug log
      await organizationService.createOrganization({ 
        id: '00000000-0000-0000-0000-000000000000', // Placeholder for new ID, backend should generate
        name: newCompanyName, 
        inn: newCompanyInn,
        icon: '', // Default empty icon
        type: newCompanyType,
        directorFullName: newCompanyDirectorFullName,
        legalAddress: newCompanyLegalAddress,
        kpp: newCompanyKpp,
        ogrn: newCompanyOgrn,
      });
      setSnackbarMessage('Компания успешно создана!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      handleCloseCreateModal();
      fetchCompanies(); // Refresh the list of companies
    } catch (err) {
      setSnackbarMessage('Ошибка при создании компании.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      console.error('Error creating organization:', err);
    } finally {
      setCreatingCompany(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <Box>
      {/* Заголовок */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
          Привет, {authenticatedUser?.manager?.phone ? `+${authenticatedUser.manager.phone}` : authenticatedUser?.user?.phone ? `+${authenticatedUser.user.phone}` : 'Менеджер'}! 👋
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Управляйте компаниями и проектами
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Секция компаний */}
        <Grid item xs={12} lg={8}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Мои компании
                </Typography>
                <Button variant="outlined" size="small">
                  Все компании
                </Button>
              </Box>

              {loading && <Typography>Загрузка компаний...</Typography>}
              {error && <Typography color="error">Ошибка: {error}</Typography>}
              {!loading && companies.length === 0 && (
                <Typography>Нет доступных компаний.</Typography>
              )}

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {companies.map((company) => (
                  <Card 
                    key={company.id} 
                    variant="outlined"
                    sx={{ 
                      '&:hover': { 
                        boxShadow: 2,
                        cursor: 'pointer'
                      }
                    }}
                  >
                    <CardContent sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                            {company.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            ИНН: {company.inn} | Тип: {OrganizationType[company.type]}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                            {/* <Typography variant="caption" color="text.secondary">
                              Сотрудников: {company.employees}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Задач: {company.tasks}
                            </Typography> */}
                          </Box>
                        </Box>
                        
                        <Box sx={{ textAlign: 'right', ml: 2 }}>
                          <Button 
                            size="small" 
                            variant="contained"
                            onClick={() => navigate(`/organization/${company.id}`)}
                          >
                            Подробнее
                          </Button>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Боковая панель: Быстрые действия */}
        <Grid item xs={12} lg={4}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Быстрые действия
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Button 
                    variant="contained" 
                    fullWidth 
                    startIcon={<AddBusiness />}
                    sx={{ justifyContent: 'flex-start' }}
                    onClick={handleOpenCreateModal}
                  >
                    Создать компанию
                  </Button>
                  <Button 
                    variant="outlined" 
                    fullWidth 
                    startIcon={<Business />}
                    sx={{ justifyContent: 'flex-start' }}
                  >
                    Посмотреть компании
                  </Button>
                </Box>
              </CardContent>
            </Card>

            {/* Уведомления (пока оставлю пустым или удалю, если не нужно) */}
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Уведомления
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* Здесь будут уведомления для менеджера */}
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Avatar sx={{ width: 32, height: 32, backgroundColor: 'primary.main' }}>
                      <Notifications sx={{ fontSize: 16 }} />
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        Новый запрос
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        На вашу компанию поступил новый запрос
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Grid>
      </Grid>

      {/* Модальное окно для создания компании */}
      <Dialog open={openCreateModal} onClose={handleCloseCreateModal}>
        <DialogTitle>Создать новую компанию</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Название компании"
            type="text"
            fullWidth
            variant="outlined"
            value={newCompanyName}
            onChange={(e) => setNewCompanyName(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="ИНН компании"
            type="text"
            fullWidth
            variant="outlined"
            value={newCompanyInn}
            onChange={(e) => setNewCompanyInn(e.target.value)}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth margin="dense">
            <InputLabel>Тип организации</InputLabel>
            <Select
              value={newCompanyType}
              label="Тип организации"
              onChange={(e) => setNewCompanyType(e.target.value as OrganizationType)}
            >
              <MenuItem value={OrganizationType.IndividualEntrepreneur}>
                Индивидуальный предприниматель
              </MenuItem>
              <MenuItem value={OrganizationType.LegalEntity}>
                Юридическое лицо
              </MenuItem>
            </Select>
          </FormControl>
          <TextField
            margin="dense"
            label="ФИО директора"
            type="text"
            fullWidth
            variant="outlined"
            value={newCompanyDirectorFullName}
            onChange={(e) => setNewCompanyDirectorFullName(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Юридический адрес"
            type="text"
            fullWidth
            variant="outlined"
            value={newCompanyLegalAddress}
            onChange={(e) => setNewCompanyLegalAddress(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="КПП"
            type="text"
            fullWidth
            variant="outlined"
            value={newCompanyKpp}
            onChange={(e) => setNewCompanyKpp(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="ОГРН"
            type="text"
            fullWidth
            variant="outlined"
            value={newCompanyOgrn}
            onChange={(e) => setNewCompanyOgrn(e.target.value)}
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateModal} color="primary">
            Отмена
          </Button>
          <Button 
            onClick={handleCreateCompany} 
            color="primary" 
            variant="contained"
            disabled={creatingCompany}
          >
            {creatingCompany ? <CircularProgress size={24} /> : 'Создать'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}>
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};
